import type { Ref } from "vue";
import type { Router } from "vue-router";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import { routerPath } from "@/common/router";
import deliveryRegisterInputStore from "@/store/deliveryRegisterInputStore";
import {
  GetPointServerInfoDocument,
  GetPointServerInfoQuery,
  GetPrefCitySectionByZipCodeDocument,
  GetPrefCitySectionByZipCodeQuery,
  GetShopDocument,
  GetShopQuery,
  GetShopQueryVariables,
  ListMemberDocument,
  ListMemberQuery,
} from "@/generated/graphql";
import type { Member } from "../types";

/**
 * 配達依頼登録開始画面の会員操作に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterStartMemberParams {
  router: Router;
  btnNonMemberToggle: Ref<number>;
  zip_code: Ref<string>;
  prefSelectValue: Ref<unknown>;
  errorZipCode: Ref<string>;
  errorPrefSelect: Ref<string>;
  userErrorMessage: Ref<string>;
  getAvailableDate: Ref<string>;
  deliveryAvailabilityStore: Ref<unknown[]>;
  wordList: Ref<Record<string, string>>;
  house_card_display: Ref<number>;
  phone_number: Ref<string>;
  user_name: Ref<string>;
  house_card: Ref<string>;
  receipt_barcode: Ref<string>;
  receipt_barcode_house_card: Ref<string>;
  register_no: Ref<number>;
  receipt_no: Ref<number>;
  payment_amount: Ref<number>;
  is_hide_other_shop_members: Ref<number>;
  listMemberResult: Ref<(ListMemberQuery["listMember"][number] | null)[]>;
  memberData: Ref<Array<{ member_id: string; name: string; phone_number: string }>>;
  dialog: Ref<boolean>;
  message: Ref<string>;
  showDeliveryAvailability: (areaCode: string | null) => Promise<void>;
}

/**
 * 配達依頼登録開始画面の会員操作を扱う composable。
 */
export const useDeliveryRequestRegisterStartMember = ({
  router,
  btnNonMemberToggle,
  zip_code,
  prefSelectValue,
  errorZipCode,
  errorPrefSelect,
  userErrorMessage,
  getAvailableDate,
  deliveryAvailabilityStore,
  wordList,
  house_card_display,
  phone_number,
  user_name,
  house_card,
  receipt_barcode,
  receipt_barcode_house_card,
  register_no,
  receipt_no,
  payment_amount,
  is_hide_other_shop_members,
  listMemberResult,
  memberData,
  dialog,
  message,
  showDeliveryAvailability,
}: UseDeliveryRequestRegisterStartMemberParams) => {
  // 新規会員入力を「郵便番号」モードに切り替える
  const onClickToggleZip = (): void => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    btnNonMemberToggle.value = 0;
    if (btnNonMemberToggle.value == 0 && zip_code.value.length === 7) {
      onZipCodeInput();
    }
  };

  // 新規会員入力を「都道府県」モードに切り替える
  const onClickTogglePref = (): void => {
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
    btnNonMemberToggle.value = 1;
  };

  // エリア選択変更時に、該当エリアの配達便一覧へ更新する
  const onChangeArea = (item: { area_code: string; area_name: string }): void => {
    showDeliveryAvailability(item.area_code);
  };

  // 郵便番号7桁入力時に対象エリアかをサーバー検証する
  const onZipCodeInput = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";

    if (zip_code.value.length != 7) {
      return;
    }

    const addressBlock: ApolloQueryResult<GetPrefCitySectionByZipCodeQuery> =
      await apolloClient.query<GetPrefCitySectionByZipCodeQuery>({
        query: GetPrefCitySectionByZipCodeDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
          zip_code: zip_code.value,
        },
        fetchPolicy: "no-cache",
      });

    if (
      !addressBlock.data.getPrefCitySectionByZipCode ||
      addressBlock.data.getPrefCitySectionByZipCode.length == 0
    ) {
      errorZipCode.value = "入力した郵便番号はご利用対象外です";
      return;
    }
  };

  // 都道府県入力時は郵便番号系エラーをリセットする
  const onPrefSelectInput = (): void => {
    zip_code.value = "";
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";
  };

  // 仮会員一覧画面へ遷移する
  const onClickTmpMemberList = async (): Promise<void> => {
    await router.push({ path: routerPath.tmpMemberList });
  };

  // 新規会員フローで入力値を検証し、入力画面へ遷移する
  const onClickDeliveryRequestAndNewMember = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();
    // 郵便番号の必須/桁数/数値チェックは Field(rules) 側で実施する。
    if (btnNonMemberToggle.value == 1 && prefSelectValue.value === "") {
      errorPrefSelect.value = "都道府県が入力されていません。";
      return;
    }

    if (btnNonMemberToggle.value == 0) {
      // 郵便番号モード: 郵便番号から住所候補を確認してストアへ保存
      if (errorZipCode.value && errorZipCode.value.length > 0) {
        return;
      }

      const addressBlock: ApolloQueryResult<GetPrefCitySectionByZipCodeQuery> =
        await apolloClient.query<GetPrefCitySectionByZipCodeQuery>({
          query: GetPrefCitySectionByZipCodeDocument,
          variables: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            shop_code: localStorageGetShopCode(),
            zip_code: zip_code.value,
          },
          fetchPolicy: "no-cache",
        });

      if (
        addressBlock.data.getPrefCitySectionByZipCode &&
        addressBlock.data.getPrefCitySectionByZipCode.length > 0
      ) {
        await deliveryRegisterInputStore.setupValue({
          zip_code: zip_code.value,
          pref_name: "",
          user_id: "",
          areaDialog: false,
          getAvailableDate: getAvailableDate.value,
          deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
          tmp_member_id: "",
        });
      } else {
        message.value =
          "郵便番号が正しくないか、または配達可能な住所がありません";
        dialog.value = true;
        return;
      }
    } else if (btnNonMemberToggle.value == 1) {
      // 都道府県モード: 選択都道府県をストアへ保存
      await deliveryRegisterInputStore.setupValue({
        zip_code: "",
        pref_name: prefSelectValue.value,
        user_id: "",
        areaDialog: true,
        getAvailableDate: getAvailableDate.value,
        deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
        tmp_member_id: "",
      });
    }

    // 配達依頼登録入力画面へ遷移
    await router.push({ path: routerPath.deliveryRequestRegisterInput });
  };

  // 既存会員フロー: 条件入力 -> 会員検索 -> 1件確定なら遷移、複数なら選択ダイアログ表示
  const onClickDeliveryRequest = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();
    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";

    if (receipt_barcode.value.length !== 16) {
      // 通常検索(電話/カナ/ハウスカード)の必須条件チェック
      if (house_card_display.value == 1) {
        if (
          phone_number.value.length < 4 &&
          user_name.value.length == 0 &&
          house_card.value.length == 0
        ) {
          userErrorMessage.value =
            "電話番号、お名前、または" +
            wordList.value["lbl_house_card"] +
            "を入力してください。";
          return;
        }
      } else {
        if (phone_number.value.length < 4 && user_name.value.length == 0) {
          userErrorMessage.value = "電話番号、またはお名前を入力してください。";
          return;
        }
      }
    } else {
      // レシートバーコード検索: 店舗のポイントサーバー設定を確認
      const getShop: ApolloQueryResult<GetShopQuery> = await apolloClient.query<
        GetShopQuery,
        GetShopQueryVariables
      >({
        query: GetShopDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
        },
      });

      if (
        getShop.data.getShop == null ||
        getShop.data.getShop.point_server_store_code == null ||
        getShop.data.getShop.point_server_store_code === "" ||
        getShop.data.getShop.point_server_ip_address == null ||
        getShop.data.getShop.point_server_ip_address === "" ||
        getShop.data.getShop.point_server_db_name == null ||
        getShop.data.getShop.point_server_db_name === ""
      ) {
        userErrorMessage.value = "ポイントサーバーの設定が正しくありません。";
        return;
      }

      const shopInfo = getShop.data.getShop;
      // レシートバーコードから会員識別情報を取得
      const pointServerInfo: ApolloQueryResult<GetPointServerInfoQuery> =
        await apolloClient.query<GetPointServerInfoQuery>({
          query: GetPointServerInfoDocument,
          variables: {
            input: {
              receipt_barcode: receipt_barcode.value,
              point_server_store_code: shopInfo.point_server_store_code,
              point_server_ip_address: shopInfo.point_server_ip_address,
              point_server_db_name: shopInfo.point_server_db_name,
            },
          },
          fetchPolicy: "no-cache",
        });

      const result = pointServerInfo.data.getPointServerInfo;
      if (result?.is_connected === false) {
        userErrorMessage.value = "ポイントサーバーに接続できません。";
        return;
      }
      if (result?.house_card === null || result?.house_card === undefined) {
        userErrorMessage.value = "レシートバーコードが正しくありません。";
        return;
      }

      receipt_barcode_house_card.value = result.house_card ?? "";
      register_no.value = result.register_no ? Number(result.register_no) : 0;
      receipt_no.value = result.receipt_no ? Number(result.receipt_no) : 0;
      payment_amount.value = result.payment_amount
        ? Number(result.payment_amount)
        : 0;
      phone_number.value = "";
      user_name.value = "";
      house_card.value = "";
    }

    // 会員検索を実行
    const listMember: ApolloQueryResult<ListMemberQuery> =
      await apolloClient.query<ListMemberQuery>({
        query: ListMemberDocument,
        variables: {
          input: {
            customer_code: localStorageGetCustomerCode(),
            chain_code: localStorageGetChainCode(),
            section_shop_code: localStorageGetShopCode(),
            is_hide_other_shop_members: is_hide_other_shop_members.value,
            phone: phone_number.value,
            kana: user_name.value,
            house_card: receipt_barcode_house_card.value || house_card.value,
            limit: 200,
            offset: 0,
            sortBy: "updated_date",
            sortOrder: "desc",
          },
        },
        fetchPolicy: "no-cache",
      });

    listMemberResult.value = listMember.data.listMember ?? [];
    if (!listMemberResult.value || listMemberResult.value.length == 0) {
      userErrorMessage.value = "該当条件の会員情報がありません。";
      return;
    }

    if (
      listMemberResult.value.length > 1 ||
      (listMemberResult.value.length === 1 &&
        listMemberResult.value[0] &&
        listMemberResult.value[0].is_deliverable === 0)
    ) {
      // 候補が複数、または配達不可会員のみの場合は選択ダイアログへ
      memberData.value = [];
      listMemberResult.value.forEach((value) => {
        if (value) {
          memberData.value.push({
            member_id: value.member_id,
            name: value.name1 + " " + value.name2,
            phone_number: value.phone,
          });
        }
      });
      dialog.value = true;
      return;
    }

    // 1件確定した会員情報をストアへ保存して入力画面へ遷移
    await deliveryRegisterInputStore.setupValue({
      zip_code: "",
      pref_name: "",
      user_id: listMemberResult.value[0]?.member_id ?? "",
      areaDialog: false,
      getAvailableDate: getAvailableDate.value,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      tmp_member_id: "",
      house_card: receipt_barcode_house_card.value || house_card.value,
      register_no: register_no.value,
      receipt_no: receipt_no.value,
      payment_amount: payment_amount.value,
    });

    await router.push({ path: routerPath.deliveryRequestRegisterInput });
  };

  // 会員候補ダイアログで選択された会員を確定する
  const onClickDeliveryRequestByUser = (
    item: Member | null | undefined
  ): void => {
    deliveryRegisterInputStore.setupValue({
      zip_code: "",
      pref_name: "",
      user_id: item?.member_id ?? "",
      areaDialog: false,
      getAvailableDate: getAvailableDate.value,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      tmp_member_id: "",
      house_card: receipt_barcode_house_card.value || house_card.value,
      register_no: register_no.value,
      receipt_no: receipt_no.value,
      payment_amount: payment_amount.value,
    });
    router.push({ path: routerPath.deliveryRequestRegisterInput });
  };

  return {
    onClickToggleZip,
    onClickTogglePref,
    onChangeArea,
    onZipCodeInput,
    onPrefSelectInput,
    onClickTmpMemberList,
    onClickDeliveryRequestAndNewMember,
    onClickDeliveryRequest,
    onClickDeliveryRequestByUser,
  };
};
